import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Loader2, Briefcase, Plus, Edit, Trash2, Save, X, Sparkles, Upload, Download } from 'lucide-react';
import { workExperienceApi, WorkExperience, WorkExperienceInput } from '@/lib/work-experience-api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

interface WorkExperienceSectionProps {
  resumePath: string | null;
  resumeFilename: string | null;
  onResumeUpload: (file: File) => Promise<void>;
  onResumeDownload: () => Promise<void>;
  onResumeRemove: () => Promise<void>;
  uploadingCV: boolean;
  userId: string;
  isReadOnly?: boolean;
}

export function WorkExperienceSection({
  resumePath,
  resumeFilename,
  onResumeUpload,
  onResumeDownload,
  onResumeRemove,
  uploadingCV,
  userId,
  isReadOnly = false,
}: WorkExperienceSectionProps) {
  const [experiences, setExperiences] = useState<WorkExperience[]>([]);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<WorkExperienceInput>>({});
  const [showPreview, setShowPreview] = useState(false);
  const [extractedJobs, setExtractedJobs] = useState<WorkExperienceInput[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);

  useEffect(() => {
    loadExperiences();
  }, [userId]);

  const loadExperiences = async () => {
    try {
      setLoading(true);
      const data = await workExperienceApi.getAll(userId);
      setExperiences(data);
    } catch (error) {
      console.error('Error loading work experience:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExtract = async () => {
    if (!resumePath) return;

    try {
      setExtracting(true);
      setExtractError(null);

      let filePath = resumePath;

      // Extract file path if it's a full URL
      if (filePath.includes('/storage/v1/object/')) {
        const match = filePath.match(/\/resumes\/(.+?)(?:\?|$)/);
        if (match) {
          filePath = match[1];
        }
      }

      console.log('Extracting from file path:', filePath);

      const extracted = await workExperienceApi.extractFromResume(filePath);

      if (extracted.length === 0) {
        setExtractError('No work experience found in resume. Try adding jobs manually.');
        return;
      }

      setExtractedJobs(extracted);
      setShowPreview(true);
    } catch (error: any) {
      console.error('Error extracting work experience:', error);
      setExtractError(error.message || 'Failed to extract work experience. Please try adding jobs manually.');
    } finally {
      setExtracting(false);
    }
  };

  const handleSaveExtracted = async () => {
    try {
      setSaving(true);
      await workExperienceApi.bulkCreate(extractedJobs);
      setShowPreview(false);
      setExtractedJobs([]);
      await loadExperiences();
    } catch (error) {
      console.error('Error saving extracted jobs:', error);
      alert('Failed to save jobs. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (exp: WorkExperience) => {
    setEditingId(exp.id);
    setEditData({
      job_title: exp.job_title,
      company_name: exp.company_name,
      start_date: exp.start_date,
      end_date: exp.end_date,
      description: exp.description,
      is_current: exp.is_current,
    });
  };

  const handleSave = async (id: string) => {
    try {
      await workExperienceApi.update(id, editData);
      setEditingId(null);
      setEditData({});
      await loadExperiences();
    } catch (error) {
      console.error('Error updating work experience:', error);
      alert('Failed to update job. Please try again.');
    }
  };

  const handleDeleteClick = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      await workExperienceApi.delete(deleteConfirmId);
      await loadExperiences();
      setDeleteConfirmId(null);
    } catch (error) {
      console.error('Error deleting work experience:', error);
      setExtractError('Failed to delete job. Please try again.');
    }
  };

  const handleCVUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const file = e.target.files?.[0];
    if (!file) return;
    await onResumeUpload(file);
    e.target.value = '';
  };

  const handleAddManual = async () => {
    const newJob: WorkExperienceInput = {
      job_title: 'New Position',
      company_name: 'Company Name',
      start_date: new Date().toISOString().split('T')[0],
      end_date: null,
      description: '',
      is_current: true,
    };

    try {
      const created = await workExperienceApi.create(newJob);
      await loadExperiences();
      setEditingId(created.id);
      setEditData(newJob);
    } catch (error) {
      console.error('Error creating work experience:', error);
      alert('Failed to add job. Please try again.');
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="rounded-2xl border border-slate-200 bg-white p-4 md:p-6 shadow-sm">
        <div className="flex items-start justify-between mb-4 gap-3">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-slate-600" />
              Work Experience
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {experiences.length > 0 ? `${experiences.length} position${experiences.length !== 1 ? 's' : ''}` : 'Build your professional profile'}
            </p>
          </div>
          <div className="flex gap-2 items-start">
            {resumeFilename ? (
              <>
                <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <span className="text-xs text-slate-700 truncate max-w-[120px]">{resumeFilename}</span>
                  <Button
                    onClick={onResumeDownload}
                    size="sm"
                    variant="ghost"
                    className="h-6 w-6 p-0"
                    disabled={uploadingCV}
                  >
                    {uploadingCV ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Download className="h-3 w-3" />
                    )}
                  </Button>
                  {!isReadOnly && (
                    <Button
                      onClick={onResumeRemove}
                      size="sm"
                      variant="ghost"
                      className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                      disabled={uploadingCV}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
                {!isReadOnly && (
                  <Button
                    onClick={handleExtract}
                    disabled={extracting || uploadingCV}
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {extracting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4 mr-2" />
                        Extract
                      </>
                    )}
                  </Button>
                )}
              </>
            ) : !isReadOnly ? (
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
                  onChange={handleCVUpload}
                  disabled={uploadingCV}
                  className="hidden"
                />
                <Button
                  size="sm"
                  className="pointer-events-none bg-emerald-600 hover:bg-emerald-700"
                  disabled={uploadingCV}
                >
                  {uploadingCV ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingCV ? 'Uploading...' : 'Upload CV'}
                </Button>
              </label>
            ) : null}
          </div>
        </div>

        {extractError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {extractError}
          </div>
        )}

        {experiences.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
            <Briefcase className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <h4 className="text-sm font-medium text-slate-900 mb-1">No work experience yet</h4>
            <p className="text-xs text-slate-500 mb-4">
              {isReadOnly
                ? 'This candidate has not added any work experience yet'
                : resumePath ? 'Extract from your resume or add manually' : 'Add your professional experience'}
            </p>
            {!isReadOnly && (
              <Button onClick={handleAddManual} size="sm" variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Job Manually
              </Button>
            )}
          </div>
        )}

        <div className="space-y-3">
          {experiences.map((exp) => (
            <div
              key={exp.id}
              className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
            >
              {editingId === exp.id ? (
                <div className="space-y-3">
                  <Input
                    value={editData.job_title || ''}
                    onChange={(e) => setEditData({ ...editData, job_title: e.target.value })}
                    placeholder="Job Title"
                    className="font-semibold"
                  />
                  <Input
                    value={editData.company_name || ''}
                    onChange={(e) => setEditData({ ...editData, company_name: e.target.value })}
                    placeholder="Company Name"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={editData.start_date || ''}
                      onChange={(e) => setEditData({ ...editData, start_date: e.target.value })}
                      placeholder="Start Date"
                    />
                    <Input
                      type="date"
                      value={editData.end_date || ''}
                      onChange={(e) => setEditData({ ...editData, end_date: e.target.value })}
                      placeholder="End Date"
                      disabled={editData.is_current}
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={editData.is_current || false}
                      onChange={(e) =>
                        setEditData({
                          ...editData,
                          is_current: e.target.checked,
                          end_date: e.target.checked ? null : editData.end_date,
                        })
                      }
                      className="rounded"
                    />
                    <span className="text-slate-600">Current Position</span>
                  </label>
                  <Textarea
                    value={editData.description || ''}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    placeholder="Job description..."
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => handleSave(exp.id)} size="sm">
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      onClick={() => {
                        setEditingId(null);
                        setEditData({});
                      }}
                      size="sm"
                      variant="outline"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 flex items-center gap-2">
                        {exp.job_title}
                        {exp.is_current && (
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-medium">
                            Present
                          </span>
                        )}
                      </h4>
                      <p className="text-sm text-slate-600">{exp.company_name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatDate(exp.start_date)} -{' '}
                        {exp.is_current ? 'Present' : formatDate(exp.end_date!)}
                      </p>
                    </div>
                    {!isReadOnly && (
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleEdit(exp)}
                          className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
                        >
                          <Edit className="h-4 w-4 text-slate-500" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(exp.id)}
                          className="p-1.5 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                  {exp.description && (
                    <p className="text-sm text-slate-700 leading-relaxed">{exp.description}</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>

        {experiences.length > 0 && !isReadOnly && (
          <Button
            onClick={handleAddManual}
            size="sm"
            variant="outline"
            className="w-full mt-3"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Another Position
          </Button>
        )}
      </div>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Review Extracted Work Experience</DialogTitle>
            <DialogDescription>
              We found {extractedJobs.length} position{extractedJobs.length !== 1 ? 's' : ''} from
              your resume. Review and edit before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4 overflow-y-auto flex-1 pr-2">
            {extractedJobs.map((job, index) => (
              <div key={index} className="border border-slate-200 rounded-lg p-4 space-y-3">
                <Input
                  value={job.job_title}
                  onChange={(e) => {
                    const updated = [...extractedJobs];
                    updated[index].job_title = e.target.value;
                    setExtractedJobs(updated);
                  }}
                  placeholder="Job Title"
                />
                <Input
                  value={job.company_name}
                  onChange={(e) => {
                    const updated = [...extractedJobs];
                    updated[index].company_name = e.target.value;
                    setExtractedJobs(updated);
                  }}
                  placeholder="Company Name"
                />
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    type="date"
                    value={job.start_date}
                    onChange={(e) => {
                      const updated = [...extractedJobs];
                      updated[index].start_date = e.target.value;
                      setExtractedJobs(updated);
                    }}
                  />
                  <Input
                    type="date"
                    value={job.end_date || ''}
                    onChange={(e) => {
                      const updated = [...extractedJobs];
                      updated[index].end_date = e.target.value;
                      setExtractedJobs(updated);
                    }}
                    disabled={job.is_current}
                  />
                </div>
                <Textarea
                  value={job.description}
                  onChange={(e) => {
                    const updated = [...extractedJobs];
                    updated[index].description = e.target.value;
                    setExtractedJobs(updated);
                  }}
                  placeholder="Description"
                  rows={3}
                />
              </div>
            ))}
          </div>
          <DialogFooter className="flex-shrink-0 mt-4">
            <Button onClick={handleSaveExtracted} disabled={saving} className="flex-1">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save All Jobs'
              )}
            </Button>
            <Button
              onClick={() => {
                setShowPreview(false);
                setExtractedJobs([]);
              }}
              variant="outline"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteConfirmId !== null} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Work Experience</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this position? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmId(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
